import { Injectable } from '@angular/core';
import {
    ReportAnalysisType,
    ReportFileNames,
} from 'app/core/enums/common.enum';
import { FilterItem, ReportAnalysisFilters } from 'app/core/interfaces/reports';
import { Observable, Subject } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ReportAnalysisService {
    private _filters: Subject<ReportAnalysisFilters> =
        new Subject<ReportAnalysisFilters>();
    private _isReport: Subject<boolean> = new Subject<boolean>();

    // -------------------------------------------------------------------------------------
    // @ Accessors
    // -------------------------------------------------------------------------------------

    /**
     * Setter & getter for filters
     *
     * @param value
     */
    set filters(value: ReportAnalysisFilters) {
        this._filters.next(value);
    }

    get filters$(): Observable<ReportAnalysisFilters> {
        return this._filters.asObservable();
    }

    /**
     * Setter & getter for is report
     *
     * @param value
     */
    set isReport(value: boolean) {
        this._isReport.next(value);
    }

    get isReport$(): Observable<boolean> {
        return this._isReport.asObservable();
    }

    constructor() {}

    // -------------------------------------------------------------------------------------
    // @ Public Methods
    // -------------------------------------------------------------------------------------

    getInventorySalesAnalysisFilters(report: any): void {
        // console.log(report);
        // Filters data
        const data = report.subcategories;
        const items = data.map((subcat: any) => subcat.products.flat()).flat();

        // Locations
        const locations = [
            ...items
                .reduce((map: Map<number, FilterItem>, item: any) => {
                    if (item.location) {
                        map.set(item.location.id, item.location);
                    }
                    return map;
                }, new Map<number, FilterItem>())
                .values(),
        ].sort((a, b) => a.name.localeCompare(b.name));

        // Categories
        const categories = [
            ...data
                .reduce((map: Map<number, FilterItem>, subcat: any) => {
                    if (subcat.category) {
                        map.set(subcat.category.id, subcat.category);
                    }
                    return map;
                }, new Map<number, FilterItem>())
                .values(),
        ].sort((a, b) => a.name.localeCompare(b.name));

        // Subcategories
        const subCategories = [
            ...data
                .reduce((map: Map<number, FilterItem>, subcat: FilterItem) => {
                    map.set(subcat.id, subcat);
                    return map;
                }, new Map<number, FilterItem>())
                .values(),
        ].sort((a, b) => a.name.localeCompare(b.name));

        // console.log(locations, categories, subCategories);
        this.filters = {
            locations,
            categories,
            subCategories,
        };
    }

    getCostAnalysisFilters(report: any): void {
        // console.log(report);
        // Filters data
        const data = report.subcategories;
        // const items = data.map((subcat: any) => subcat.products.flat()).flat();

        // Categories
        const categories = [
            ...data
                .reduce((map: Map<number, FilterItem>, subcat: any) => {
                    if (subcat.category) {
                        map.set(subcat.category.id, subcat.category);
                    }
                    return map;
                }, new Map<number, FilterItem>())
                .values(),
        ].sort((a, b) => a.name.localeCompare(b.name));

        // Subcategories
        const subCategories = [
            ...data
                .reduce((map: Map<number, FilterItem>, subcat: FilterItem) => {
                    map.set(subcat.id, { id: subcat.id, name: subcat.name });
                    return map;
                }, new Map<number, FilterItem>())
                .values(),
        ].sort((a, b) => a.name.localeCompare(b.name));

        // console.log(categories, subCategories);
        this.filters = {
            categories,
            subCategories,
        };
    }

    getReportAnalysisType(
        filename: ReportFileNames
    ): ReportAnalysisType | undefined {
        const map: Partial<Record<ReportFileNames, ReportAnalysisType>> = {
            [ReportFileNames.finalReport]: ReportAnalysisType.FINAL_REPORT,
            [ReportFileNames.inventoryAnalysis]:
                ReportAnalysisType.INV_ANALYSIS,
            [ReportFileNames.costAnalysis]: ReportAnalysisType.COST_ANALYSIS,
        };

        return map[filename];
    }
}
